/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import * as Msdyn365 from '@msdyn365-commerce/core';
import { getTelemetryObject, IModuleProps, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import { IAaaAccountOrderHistoryTileData } from './aaa_account-order-history-tile.data';
import { IAaa_accountOrderHistoryTileProps } from './aaa_account-order-history-tile.props.autogenerated';
import DescriptionComponent from './components/aaa_account-order-history-tile-description';
import LinksComponent from './components/aaa_account-order-history-tile-links';
import { getRequestOrderHistoryB2CAsync } from '../../dataActions/DataActionExtension.g';

export interface IAccountWishlistTileViewProps extends IAaa_accountOrderHistoryTileProps<IAaaAccountOrderHistoryTileData> {
    AccountWishlistTile: IModuleProps;
    className: string;
    heading?: React.ReactNode;
    links?: React.ReactNode;
    description?: React.ReactNode;
}

/**
 *
 * AccountWishlist component.
 * @extends {React.PureComponent<IAaa_accountOrderHistoryTileProps<IAaaAccountOrderHistoryTileData >>}
 */
class AccountOrderHistoryTile extends React.PureComponent<IAaa_accountOrderHistoryTileProps<IAaaAccountOrderHistoryTileData>, any> {
    private readonly telemetryContent?: ITelemetryContent;

    constructor(props: IAaa_accountOrderHistoryTileProps<IAaaAccountOrderHistoryTileData>) {
        super(props);
        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
        this.state = {
            orderHistoryCount: 0
        };
    }
    public componentDidMount() {
        void this.getOrderHistoryCount();
    }

    private readonly getOrderHistoryCount = async () => {
        const { context } = this.props;
        if (context.actionContext.requestContext.user.isAuthenticated) {
            const orderHistorys: any = await getRequestOrderHistoryB2CAsync(
                {
                    callerContext: this.props.context.actionContext,
                    queryResultSettings: {}
                },
                ''
            );
            this.setState({ orderHistoryCount: orderHistorys.RequestOrder.length });
        }
    };
    public render(): JSX.Element | null {
        const { config, resources } = this.props;
        const { className, heading, links } = config;
        const { accountOrderHistoryTileDescription } = resources;
        const description = accountOrderHistoryTileDescription.replace(
            '{count}',
            this.state.orderHistoryCount ? this.state.orderHistoryCount : 'no'
        );

        const viewProps = {
            ...this.props,
            className,
            AccountWishlistTile: {
                moduleProps: this.props,
                className: classnames('ms-account-order-history-tile', config.className)
            },
            heading: heading && heading.text && (
                <Msdyn365.Text
                    className='ms-account-order-history-tile__heading'
                    tag={heading.tag}
                    text={heading.text}
                    editProps={{ onEdit: this.handleHeadingChange, requestContext: this.props.context.request }}
                />
            ),
            links: links && links.length > 0 && (
                <LinksComponent
                    {...{
                        links,
                        onTextChange: this.handleLinkTextChange,
                        requestContext: this.props.context.request,
                        telemetryContent: this.telemetryContent
                    }}
                />
            ),
            description: <DescriptionComponent description={description} />
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    public handleHeadingChange = (event: Msdyn365.ContentEditableEvent) => (this.props.config.heading.text = event.target.value);

    public handleLinkTextChange = (linkIndex: number) => (event: Msdyn365.ContentEditableEvent) => {
        if (this.props.config.links && this.props.config.links[linkIndex]) {
            this.props.config.links[linkIndex].linkText = event.target.value;
        }
    };
}

export default AccountOrderHistoryTile;
